import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { orpc } from "~/lib/orpc-client";
import { Skeleton } from "@alchema/ui";
import { EmptyState } from "~/components/shared/EmptyState";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/analytics/")({
  component: OverviewPage,
});

const FUNNEL_STAGES = [
  { key: "applied" as const, label: () => m.analytics_funnel_stage_applied(), color: "var(--accent)" },
  { key: "screening" as const, label: () => m.analytics_funnel_stage_screening(), color: "var(--warning)" },
  { key: "interview" as const, label: () => m.analytics_funnel_stage_interview(), color: "var(--success)" },
  { key: "offer" as const, label: () => m.analytics_funnel_stage_offer(), color: "var(--success)" },
  { key: "rejected" as const, label: () => m.analytics_funnel_stage_rejected(), color: "var(--error)" },
  { key: "withdrawn" as const, label: () => m.analytics_funnel_stage_withdrawn(), color: "var(--text-muted)" },
];

function FunnelEmptyIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 4h18l-7 9v6l-4-2v-4L3 4z" />
    </svg>
  );
}

function BoardEmptyIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}

function OverviewPage() {
  const funnelQuery = useQuery(orpc.analytics.funnel.queryOptions());
  const boardQuery = useQuery(orpc.analytics.boardRates.queryOptions());

  // Derive top-level stats from funnel data
  const stats = funnelQuery.data
    ? (() => {
        const d = funnelQuery.data;
        const total = d.applied;
        const responses = d.screening + d.interview + d.offer;
        const responseRate = total > 0 ? Math.round((responses / total) * 100) : 0;
        const interviewRate = total > 0 ? Math.round((d.interview / total) * 100) : 0;
        return { total, responseRate, interviewRate };
      })()
    : null;

  return (
    <div className="space-y-8">
      {/* Stat row */}
      {funnelQuery.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : stats && stats.total > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <StatBlock label={m.analytics_funnel_stage_applied()} value={String(stats.total)} />
          <StatBlock label="Response Rate" value={`${stats.responseRate}%`} />
          <StatBlock label="Interview Rate" value={`${stats.interviewRate}%`} />
        </div>
      ) : null}

      {/* Application Funnel */}
      <div>
        <p className="section-label mb-4">{m.analytics_funnel_title()}</p>
        <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
          {m.analytics_funnel_desc()}
        </p>

        {funnelQuery.isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-8 rounded" />
            ))}
          </div>
        ) : funnelQuery.data && funnelQuery.data.applied > 0 ? (
          <div
            className="rounded-xl overflow-hidden"
            style={{ border: "1px solid var(--border-subtle)" }}
          >
            {(() => {
              const data = funnelQuery.data;
              const max = Math.max(
                ...FUNNEL_STAGES.map((s) => data[s.key]),
                1,
              );
              return FUNNEL_STAGES.map((stage, idx) => {
                const value = data[stage.key];
                const pct = (value / max) * 100;
                const isLast = idx === FUNNEL_STAGES.length - 1;
                return (
                  <div
                    key={stage.key}
                    className="flex items-center gap-4 px-5 py-3"
                    style={{
                      borderBottom: isLast ? "none" : "1px solid var(--border-subtle)",
                      background: "var(--surface)",
                    }}
                  >
                    <span
                      className="w-24 text-xs font-medium shrink-0"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {stage.label()}
                    </span>
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--surface-raised)" }}>
                      <div
                        className="h-2 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.max(pct, value > 0 ? 4 : 0)}%`,
                          background: stage.color,
                          opacity: 0.85,
                        }}
                      />
                    </div>
                    <span
                      className="w-8 text-right text-sm font-semibold tabular-nums shrink-0"
                      style={{ color: "var(--text)" }}
                    >
                      {value}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        ) : (
          <EmptyState
            icon={<FunnelEmptyIcon />}
            title={m.analytics_funnel_empty()}
          />
        )}
      </div>

      {/* Board Response Rates */}
      <div>
        <p className="section-label mb-4">{m.analytics_board_title()}</p>
        <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
          {m.analytics_board_desc()}
        </p>

        {boardQuery.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded" />
            ))}
          </div>
        ) : boardQuery.data && boardQuery.data.length > 0 ? (
          <div
            className="rounded-xl overflow-x-auto"
            style={{ border: "1px solid var(--border-subtle)" }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-subtle)", background: "var(--surface)" }}>
                  <th className="px-5 py-3 text-left section-label">{m.analytics_board_col_source()}</th>
                  <th className="px-5 py-3 text-left section-label">{m.analytics_board_col_method()}</th>
                  <th className="px-5 py-3 text-right section-label">{m.analytics_board_col_total()}</th>
                  <th className="px-5 py-3 text-right section-label">{m.analytics_board_col_responses()}</th>
                  <th className="px-5 py-3 text-right section-label">{m.analytics_board_col_rate()}</th>
                </tr>
              </thead>
              <tbody>
                {boardQuery.data.map((row, i) => {
                  const isLast = i === boardQuery.data!.length - 1;
                  return (
                    <tr
                      key={i}
                      style={{
                        borderBottom: isLast ? "none" : "1px solid var(--border-subtle)",
                        background: "var(--surface)",
                      }}
                    >
                      <td className="px-5 py-3 font-medium" style={{ color: "var(--text)" }}>
                        {row.source}
                      </td>
                      <td className="px-5 py-3 capitalize text-xs" style={{ color: "var(--text-secondary)" }}>
                        {row.method}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>
                        {row.total}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>
                        {row.responses}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <RateBadge rate={row.rate} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<BoardEmptyIcon />}
            title={m.analytics_board_empty()}
          />
        )}
      </div>
    </div>
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="rounded-xl px-5 py-4"
      style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
    >
      <p className="section-label mb-1">{label}</p>
      <p
        className="text-2xl font-bold tabular-nums tracking-tight"
        style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
      >
        {value}
      </p>
    </div>
  );
}

function RateBadge({ rate }: { rate: number }) {
  const color =
    rate >= 30
      ? "var(--success)"
      : rate >= 10
        ? "var(--warning)"
        : "var(--error)";
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{
        color,
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
      }}
    >
      {rate}%
    </span>
  );
}
